import { useFormContext, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { type UserOrderResponseWithFlags } from '@redteclab/api/clients/bully'

import {
  CONTACT_FORM_SUBJECT_KEY,
  directContactFormSubjectPolicyGetByTranslatedSubject,
} from '../../direct-contact'
import {
  useApiOrdersSwrGetMiraklOrders,
  userSubOrderAccessorIsMarketplaceOrder,
} from '../../order'
import { type DirectContactFormValues } from '../components/DirectContactForm/DirectContactFormModel'

export const useDirectContactFormMiraklReturnAvailability = (
  orders: UserOrderResponseWithFlags[],
): {
  availableOrderIds: ReadonlySet<string>
  isLoading: boolean
} => {
  const intl = useIntl()
  const { control } = useFormContext<DirectContactFormValues>()
  const subject = useWatch({ control, name: 'subject' })
  const shouldFetchMiraklOrders =
    directContactFormSubjectPolicyGetByTranslatedSubject({
      intl,
      subject,
    }).key === CONTACT_FORM_SUBJECT_KEY.RETURN_ORDER
  const commercialOrderIds = shouldFetchMiraklOrders
    ? orders.flatMap((order) =>
        (order.subOrders ?? []).flatMap((subOrder) =>
          subOrder.subOrderId &&
          userSubOrderAccessorIsMarketplaceOrder(subOrder)
            ? // eslint-disable-next-line unicorn/no-unnecessary-array-flat-map -- don't reference, refactor if touched
              [subOrder.subOrderId]
            : [],
        ),
      )
    : []
  const { data: miraklOrders, error } = useApiOrdersSwrGetMiraklOrders({
    commercialOrderIds,
  })

  return {
    availableOrderIds: new Set(
      miraklOrders
        ?.filter((miraklOrder) => miraklOrder.returnAvailable)
        .map((miraklOrder) => miraklOrder.commercialOrderId),
    ),
    isLoading:
      commercialOrderIds.length > 0 &&
      miraklOrders === undefined &&
      error === undefined,
  }
}
